// Performance optimization utilities
export const preloadCriticalAssets = () => {
  // Only preload absolutely critical assets for initial render
  const criticalAssets = [
    '/favicon.svg'
  ];

  criticalAssets.forEach(asset => {
    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = asset.endsWith('.png') || asset.endsWith('.svg') || asset.endsWith('.jpg') || asset.endsWith('.jpeg') ? 'image' : 'fetch';
    link.href = asset;
    document.head.appendChild(link);
  });
};

// Progressive asset loading based on viewport and user interaction
export const preloadAssetsOnInteraction = () => {
  const interactionAssets = [
    '/attached_assets/Copy of RedHelicopter_Color_Logos_1750175727279.png',
    '/attached_assets/Agency_1750996305194.jpeg',
    '/attached_assets/Build Your Helicopter_v2 (4)_1751007062751.jpg'
  ];

  // Only preload after user interaction to avoid blocking initial render
  const preloadAfterInteraction = () => {
    interactionAssets.forEach(asset => {
      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'image';
      link.href = asset;
      document.head.appendChild(link);
    });
  };

  // Listen for first user interaction
  ['mousedown', 'touchstart', 'keydown', 'scroll'].forEach(event => {
    document.addEventListener(event, preloadAfterInteraction, { once: true, passive: true });
  });
};

// Lazy load heavy media assets only when needed
export const createLazyMediaLoader = () => {
  const heavyAssets = new Map<string, Promise<string>>();
  
  return {
    loadVideo: (src: string): Promise<string> => {
      if (heavyAssets.has(src)) {
        return heavyAssets.get(src)!;
      }
      
      const promise = new Promise<string>((resolve, reject) => {
        const video = document.createElement('video');
        video.preload = 'none'; // Don't preload until actually needed
        video.onloadstart = () => resolve(src);
        video.onerror = reject;
        video.src = src;
      });
      
      heavyAssets.set(src, promise);
      return promise;
    },
    
    loadImage: (src: string): Promise<string> => {
      if (heavyAssets.has(src)) {
        return heavyAssets.get(src)!;
      }
      
      const promise = new Promise<string>((resolve, reject) => {
        const img = new Image();
        img.loading = 'lazy'; // Native lazy loading
        img.onload = () => resolve(src);
        img.onerror = reject;
        img.src = src;
      });
      
      heavyAssets.set(src, promise);
      return promise;
    }
  };
};

// Enhanced audio preloading with error handling
export const preloadAudio = (src: string): Promise<HTMLAudioElement> => {
  return new Promise((resolve, reject) => {
    const audio = new Audio();
    audio.preload = 'metadata';
    audio.oncanplaythrough = () => resolve(audio);
    audio.onerror = reject;
    audio.src = src;
  });
};

// Enhanced video preloading with error handling
export const preloadVideo = (src: string): Promise<HTMLVideoElement> => {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.oncanplaythrough = () => resolve(video);
    video.onerror = reject;
    video.src = src;
  });
};

// Image compression utility
export const optimizeImageSrc = (src: string, quality = 85) => {
  // For production, this could integrate with image optimization services
  return src;
};

// Enhanced intersection observer with performance optimizations
export const createIntersectionObserver = (callback: (entries: IntersectionObserverEntry[]) => void) => {
  if (typeof window === 'undefined' || !('IntersectionObserver' in window)) {
    return null;
  }

  return new IntersectionObserver(callback, {
    rootMargin: '50px 0px',
    threshold: 0.1
  });
};

// Debounce function for performance
export const debounce = <T extends (...args: any[]) => void>(
  func: T,
  wait: number
): ((...args: Parameters<T>) => void) => {
  let timeout: NodeJS.Timeout;
  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

// Throttle function for scroll/resize events
export const throttle = <T extends (...args: any[]) => void>(
  func: T,
  limit: number
): ((...args: Parameters<T>) => void) => {
  let inThrottle: boolean;
  return (...args: Parameters<T>) => {
    if (!inThrottle) {
      func(...args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  };
};

// Component lazy loading utility
export const createLazyComponent = <T extends any>(
  factory: () => Promise<{ default: T }>
) => {
  // Note: This should be used with React.lazy() in the component file
  return factory;
};

// Viewport detection for responsive optimizations
export const getViewportSize = () => {
  if (typeof window === 'undefined') {
    return { width: 1024, height: 768, isMobile: false, isTablet: false, isDesktop: true };
  }
  
  return {
    width: window.innerWidth,
    height: window.innerHeight,
    isMobile: window.innerWidth < 768,
    isTablet: window.innerWidth >= 768 && window.innerWidth < 1024,
    isDesktop: window.innerWidth >= 1024,
  };
};

// Performance monitoring utility
export const measurePerformance = (name: string, fn: () => void) => {
  if (typeof performance !== 'undefined' && process.env.NODE_ENV === 'development') {
    const start = performance.now();
    fn();
    const end = performance.now();
    console.log(`${name} took ${end - start} milliseconds`);
  } else {
    fn();
  }
};

// Memory cleanup utility
export const cleanupResources = (cleanupFunctions: Array<() => void>) => {
  cleanupFunctions.forEach(cleanup => {
    try {
      cleanup();
    } catch (error) {
      console.warn('Error during resource cleanup:', error);
    }
  });
};

// Efficient DOM updates using RAF
export const scheduleUpdate = (callback: () => void) => {
  if (typeof requestAnimationFrame !== 'undefined') {
    requestAnimationFrame(callback);
  } else {
    setTimeout(callback, 16); // Fallback for older browsers
  }
};

// Resource usage monitoring
export const getResourceUsage = () => {
  if (typeof performance !== 'undefined' && 'memory' in performance) {
    const memory = (performance as any).memory;
    return {
      usedJSHeapSize: memory.usedJSHeapSize,
      totalJSHeapSize: memory.totalJSHeapSize,
      jsHeapSizeLimit: memory.jsHeapSizeLimit,
      usage: ((memory.usedJSHeapSize / memory.jsHeapSizeLimit) * 100).toFixed(2)
    };
  }
  return null;
};